import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import { useTheme } from '@mui/material/styles';
import { ToggleButton, ToggleButtonGroup, Grid, Box, Typography } from '@mui/material';
import { styled } from "@mui/material/styles";
import { DataGrid } from '@mui/x-data-grid';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridViewIcon from '@mui/icons-material/GridView';
import { SpeechConfigCard } from '../../components/speechConfig/Card';
import { useSpeechConfig } from './hooks';
import { makeStyles } from "tss-react/mui";

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    padding: theme.spacing(1),
    height: '30px',
    '&.Mui-selected': {
        backgroundColor: 'var(--mui-palette-primary-orange)',
        color: 'var(--mui-palette-common-white)',
        '&:hover': {
            backgroundColor: 'var(--mui-palette-primary-orange)',
        },
    },
}));

const useStyles = makeStyles()((theme) => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    heading: {
        margin: '0',
        fontWeight: '700',
        lineHeight: '1.5',
        fontSize: '1.8rem',
    },
    toggleButtonContainer: {
        display: 'flex',
        justifyContent: 'center',
    },
    create: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '0',
        marginBottom: '0',
        marginLeft: '5px',
    },
    courses: {
        marginTop: '2rem',
    },
    label: {
        color: 'var(--mui-palette-common-black)',
        fontWeight: 'bold',
    },
    inputLabel: {
        color: 'var(--mui-palette-primary-darkGrey)',
        marginBottom: '5px',
        fontWeight: '600',
        fontSize: '14px',
    },
    gridContainer: {},
    suggestionBox: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '10px',
    },
    suggestionItem: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '2px',
        cursor: 'pointer',
        boxShadow: '0 4px 8px var(--mui-palette-secondary-boxShadow1)',
        backgroundColor: 'var(--mui-palette-gray-lightGrey300)',
    },
    uploadBtn: {
        width: '50px',
        height: '50px',
        borderRadius: '5px',
    },
    hiddenInput: {
        display: 'none',
    },
    dataGridContainer: {
        minHeight: 500,
        width: '100%',
        overflow: 'auto',
    },
    dataGrid: {
        backgroundColor: 'var(--mui-palette-common-white)',
        '& .css-yrdy0g-MuiDataGrid-columnHeaderRow': {
            background: 'var(--mui-palette-primary-blue) !important',
            color: 'var(--mui-palette-action-active)',
        },
        '& .MuiDataGrid-columnHeader': {
            background: 'var(--mui-palette-primary-blue) !important',
            color: 'var(--mui-palette-common-white)',
            '&:focus': {
                outline: 'none',
            },
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '14px',
            fontWeight: 'bold',
        },
        '& .MuiSvgIcon-root': {
            color: 'var(--mui-palette-common-white)',
        },
    },
    toggleButtonGroup: {
        display: 'flex',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'row',
        },
    },
    toggleButtonSm: {
        padding: '5px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '0.7rem',
        },
    },
    gridContainer: {
        position: 'relative',
        zIndex: 1,
        marginLeft: '-8px',
        marginRight: '-8px',
        width: 'auto',
    },
}));

export const SpeechConfig = () => {
    const theme = useTheme();
    const { classes } = useStyles();
    const { loadSpeechConfig, speechConfigList, loading } = useSpeechConfig();
    const [view, setView] = useState('card');
    const [speechConfig, setSpeechConfig] = useState();

    useEffect(() => {
        loadSpeechConfig();
    }, [])


    useEffect(() => {
        setSpeechConfig(speechConfigList)
    }, [speechConfigList])

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setView(newView);
        }
    };

    const columns = [
        { field: 'geographicalType', headerName: 'Geographical Type', flex: 1 },
        {
            field: 'generation1',
            headerName: 'Generation 1',
            flex: 1,
            renderCell: (params) => {
                const { voice, wpm, accent } = params.value || {};
                return (
                    <div>
                        <p><b>Voice:</b> {voice || 'N/A'}, <b>WPM:</b> {wpm || 'N/A'}, <b>Accent:</b> {accent || 'N/A'}</p>
                    </div>
                );
            }
        },
        {
            field: 'generation2',
            headerName: 'Generation 2',
            flex: 1,
            renderCell: (params) => {
                const { voice, wpm, accent } = params.value || {};
                return (
                    <div>
                        <p><b>Voice:</b> {voice || 'N/A'}, <b>WPM:</b> {wpm || 'N/A'}, <b>Accent:</b> {accent || 'N/A'}</p>
                    </div>
                );
            }
        },
        {
            field: 'generation3',
            headerName: 'Generation 3',
            flex: 1,
            renderCell: (params) => {
                const { voice, wpm, accent } = params.value || {};
                return (
                    <div>
                        <p><b>Voice:</b> {voice || 'N/A'}, <b>WPM:</b> {wpm || 'N/A'}, <b>Accent:</b> {accent || 'N/A'}</p>
                    </div>
                );
            }
        },

    ];

    const rows = speechConfig?.map((type) => ({
        id: type?.id,
        geographicalType: type?.geographicalType,
        generation1: {
            voice: type.learningGeneration[0]?.gender,
            wpm: type.learningGeneration[0]?.words_per_minute,
            accent: type.learningGeneration[0]?.accent,
        },
        generation2: {
            voice: type.learningGeneration[1]?.gender,
            wpm: type.learningGeneration[1]?.words_per_minute,
            accent: type.learningGeneration[1]?.accent,
        },
        generation3: {
            voice: type.learningGeneration[2]?.gender,
            wpm: type.learningGeneration[2]?.words_per_minute,
            accent: type.learningGeneration[2]?.accent,
        },
    }));

    return (
        <>
            <div>
                {loading ? <Loader /> : <>
                    <div className='course'>
                        <Box className={classes.root} mb={2}>
                            <Typography className={classes.heading} align="left" variant="h1" color="primary.blue">Speech Configuration</Typography>
                            <Box className={classes.toggleButtonGroup}>
                                <ToggleButtonGroup className={classes.toggleButtonContainer}
                                    value={view}
                                    exclusive
                                    onChange={handleViewChange}
                                    aria-label="course view"
                                >
                                    <StyledToggleButton value="card" aria-label="card view" title="Card View" className={classes.toggleButtonSm}>
                                        <DashboardIcon />
                                    </StyledToggleButton>
                                    <StyledToggleButton value="grid" aria-label="grid view" title="Grid View" className={classes.toggleButtonSm}>
                                        <GridViewIcon />
                                    </StyledToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        {view === 'card' ? (
                            <Grid container className={classes.gridContainer}>
                                {speechConfig?.map(type => (
                                    <Grid key={type?.id} item xs={12} sm={6} md={4} lg={3} sx={{ p: 1 }}>
                                        <SpeechConfigCard data={type} />
                                    </Grid>
                                ))}
                            </Grid>

                        ) : (
                            <div className={classes.dataGridContainer}>
                                <DataGrid
                                    rows={rows}
                                    columns={columns}
                                    className={classes.dataGrid}
                                    pageSize={5} />
                            </div>
                        )}
                    </div>
                </>}
            </div>
        </>
    );
}